import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, ToggleLeft, ToggleRight, Mail } from 'lucide-react';
import Card from './ui/Card';
import Input from './ui/Input';
import Button from './ui/Button';
import useStore from '../store/useStore';

const topics = [
  'Pricing Updates',
  'New Features',
  'Competitor News',
  'Industry Trends',
  'Product Updates',
  'Support Information',
];

// Mock notifications
const mockNotifications = [
  { id: 1, topic: 'New Features', message: 'Introducing AI-powered deal insights!' },
  { id: 2, topic: 'Competitor News', message: 'TechCorp has released a new product line.' },
  { id: 3, topic: 'Industry Trends', message: 'Latest report on SaaS market growth is now available.' },
];

function NotificationCenter() {
  const { users, addUser, updateUserSubscriptions } = useStore();
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const handleTopicToggle = (topic: string) => {
    setSelectedTopics(prev =>
      prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
    );
  };

  const handleAddUser = () => {
    if (newUserEmail && !users.some(user => user.email === newUserEmail)) {
      addUser({ email: newUserEmail, subscribedTopics: selectedTopics });
      setNewUserEmail('');
      setSelectedTopics([]);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-6 sm:mb-8 text-center">Notification Center</h1>
      
      <Card title="Manage Subscriptions" className="mb-6 sm:mb-8">
        <div>
          <h3 className="text-lg font-semibold mb-4 text-blue-700">Select topics to subscribe:</h3>
          <ul className="space-y-2 mb-4">
            {topics.map(topic => (
              <li key={topic} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                <span className="text-blue-700 text-sm">{topic}</span>
                <Button
                  onClick={() => handleTopicToggle(topic)}
                  variant={selectedTopics.includes(topic) ? 'primary' : 'outline'}
                  className="text-xs sm:text-sm"
                >
                  {selectedTopics.includes(topic) ? (
                    <>
                      <ToggleRight className="mr-1 sm:mr-2" size={16} />
                      <span className="hidden sm:inline">Subscribed</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="mr-1 sm:mr-2" size={16} />
                      <span className="hidden sm:inline">Subscribe</span>
                    </>
                  )}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
            <Input
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full sm:flex-grow"
            />
            <Button onClick={handleAddUser} disabled={!newUserEmail || selectedTopics.length === 0} className="w-full sm:w-auto">
              <Mail size={20} className="mr-2" />
              Subscribe
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Current Subscriptions" className="mb-6 sm:mb-8">
        {users.length > 0 ? (
          <ul className="space-y-4">
            {users.map(user => (
              <li key={user.email} className="bg-blue-50 p-4 rounded-lg">
                <p className="font-semibold mb-2 text-blue-700">{user.email}</p>
                <p className="text-sm text-blue-600">
                  Subscribed to: {user.subscribedTopics.join(', ')}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-blue-600">No active subscriptions.</p>
        )}
      </Card>

      <Card title="Recent Notifications">
        <ul className="space-y-4">
          {mockNotifications.map(notification => (
            <motion.li
              key={notification.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start bg-blue-50 p-3 rounded-lg"
            >
              <Bell className="text-blue-600 mr-2 mt-1 flex-shrink-0" size={20} />
              <div>
                <span className="font-semibold text-blue-800">{notification.topic}</span>
                <p className="text-blue-600 text-sm">{notification.message}</p>
              </div>
            </motion.li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export default NotificationCenter;